import { computed, inject, Injectable, signal } from '@angular/core';
import { Store } from '@ngrx/store';
import { AccountType, PaginatedResponse } from '@qid/core';
import { firstValueFrom } from 'rxjs';
import { AuthSelectors } from '../../authentication';
import { AccountShareApi } from '../account-share.api';
import { AccountShare } from '../interface/account-share.interface';
import { Pagination } from '../../../interface/pagination.interface';
import { AccountShareQuery } from '../interface/account-share-query.interface';

@Injectable({
  providedIn: 'root'
})
export class AccountShareRepositoryService {

  private _records = signal<AccountShare[]>([]);
  readonly records = computed(() => this._records())

  private _pagination = signal<Pagination>({})
  readonly pagination = computed(() => this._pagination())

  private _isLoading = signal<boolean>(false)
  readonly isLoading = computed(() => this._isLoading())

  private accountWiseData = [];

  /** Last account `init()` loaded for; used to avoid list skeleton blink on revisiting Home for the same org */
  private lastInitAccountId: string | null = null;

  private readonly store = inject(Store);
  private readonly apiService = inject(AccountShareApi);

  constructor() { }

  async init() {
    const activeAccountId = await firstValueFrom(this.store.select(AuthSelectors.selectActiveAccountId));
    const activeAccountType = await firstValueFrom(this.store.select(AuthSelectors.selectActiveAccountType));

    if (!activeAccountId) {
      this.lastInitAccountId = null;
      return;
    }

    if (!this.accountWiseData[activeAccountId]) {
      this.addAccount(activeAccountId, activeAccountType);
    }

    const sameAccountRevisit = this.lastInitAccountId === activeAccountId;

    if (!sameAccountRevisit) {
      // Clear immediately when switching org (or first load) so wrong-account rows never flash
      this._records.set([]);
      this._pagination.set({});
      this.lastInitAccountId = activeAccountId;
    }

    await this.fetchRecordsPaginated(activeAccountId, activeAccountType, {}, {
      showLoadingSpinner: !sameAccountRevisit,
    });
  }

  async fetchRecordsPaginated(
    accountId: string,
    accountType: AccountType,
    query: AccountShareQuery = {},
    fetchOptions: { showLoadingSpinner?: boolean } = {}
  ) {
    const listQuery: AccountShareQuery = { ...query };
    if (listQuery.page == null || Number(listQuery.page) < 1) {
      listQuery.page = 1;
    }

    const defaultSpinnerFirstPageOnly = Number(listQuery.page) === 1;
    const showLoadingSpinner =
      fetchOptions.showLoadingSpinner ?? defaultSpinnerFirstPageOnly;

    if (showLoadingSpinner) {
      this.setIsLoading(true);
    }

    try {
      const res: PaginatedResponse<AccountShare> = await this.apiService.readAll(accountId, accountType, listQuery);

      if (!res || !res.docs) {
        // Handle empty response by clearing signals
        this.setRecords(accountId, []);
        this._pagination.set({});
        return;
      }

      if (listQuery.page === 1) {
        this.setRecords(accountId, res.docs);
      } else {
        this.setRecords(accountId, [...this.getRecords(accountId), ...res.docs]);
      }
      this.setPagination(accountId, res);
    } catch (error) {
      // Handle permission errors (403) or other API failures by clearing signals
      console.error('Error fetching account shares:', error);
      this.setRecords(accountId, []);
      this._pagination.set({});
    } finally {
      if (showLoadingSpinner) {
        this.setIsLoading(false);
      }
    }
  }

  async fetchNextPage(query: AccountShareQuery) {
    if (!query) {
      return;
    }
    const activeAccountId = await firstValueFrom(this.store.select(AuthSelectors.selectActiveAccountId));
    const activeAccountType = await firstValueFrom(this.store.select(AuthSelectors.selectActiveAccountType));
    const accountWiseData = this.getAccountWiseData(activeAccountId);
    if (accountWiseData.pagination.hasNext) {
      return this.fetchRecordsPaginated(activeAccountId, activeAccountType, { ...query, page: +accountWiseData.pagination.page + 1 });
    }
  }

  async refresh(query: AccountShareQuery = { page: 1 }) {
    const activeAccountId = await firstValueFrom(this.store.select(AuthSelectors.selectActiveAccountId));
    const activeAccountType = await firstValueFrom(this.store.select(AuthSelectors.selectActiveAccountType));
    await this.fetchRecordsPaginated(activeAccountId, activeAccountType, query, {
      showLoadingSpinner: true,
    });
  }

  setAccountWiseData(accountId: string, accountWiseData: AccountWiseData<AccountShare>) {
    this.accountWiseData[accountId] = accountWiseData;
  }

  setPagination(accountId: string, PaginatedResponse: PaginatedResponse<AccountShare>) {

    const pagination: Pagination = {
      totalDocs: PaginatedResponse.totalDocs,
      page: PaginatedResponse.page,
      totalPages: PaginatedResponse.totalPages,
      size: PaginatedResponse.size,
      hasNext: PaginatedResponse.hasNext,
      hasPrevious: PaginatedResponse.hasPrevious,
    };
    this.accountWiseData[accountId].pagination = pagination;
    this._pagination.set(pagination);
  }

  getAccountWiseData(accountId: string): AccountWiseData<AccountShare> {
    return this.accountWiseData[accountId];
  }

  getRecords(accountId: string): AccountShare[] {
    return this.accountWiseData[accountId].records;
  }

  setRecords(accountId: string, records: AccountShare[], pagination?: Pagination) {
    this.accountWiseData[accountId].records = records;
    if (pagination) {
      this.accountWiseData[accountId].pagination = pagination;
    }
    this._records.set(records);
    this._pagination.set(pagination);
  }

  addAccount(accountId: string, accountType: AccountType) {

    if (this.accountWiseData[accountId]) {
      return;
    }

    this.accountWiseData[accountId] = {
      account_id: accountId,
      accountType: accountType,
      records: [],
    }
  }

  setIsLoading(isLoading: boolean) {
    this._isLoading.set(isLoading);
  }

  async deleteRecord(shareId: string) {
    const activeAccountId = await firstValueFrom(this.store.select(AuthSelectors.selectActiveAccountId));
    const cuurentAccountRecords = this.getRecords(activeAccountId);
    const recordIndex = cuurentAccountRecords.findIndex(record => record.shareId === shareId);
    if (recordIndex === -1) {
      return;
    }
    cuurentAccountRecords.splice(recordIndex, 1);
    this.setRecords(activeAccountId, cuurentAccountRecords);
  }

}


interface AccountWiseData<RecordType> {
  account_id: string;
  accountType: AccountType;
  records: RecordType[];
  pagination: Pagination;
}